/**
 * formState keys that may be interpolated into a data-source query or
 * body, but not into the endpoint or headers. Endpoint interpolation does
 * not URL-encode, so a host value such as "Economics & Finance" would
 * break the URL, and headers are limited to query/body by the same rule.
 * The set rides on the interpolation context as a non-enumerable symbol
 * so templates cannot read it and object spreads used by query and body
 * keep the real formState.
 */
export const endpointExcludedFormKeysKey: unique symbol = Symbol.for(
  'de.endpointExcludedFormKeys',
);

export function withEndpointExcludedFormKeys(
  context: Record<string, unknown>,
  keys: ReadonlySet<string> | undefined,
): Record<string, unknown> {
  if (!keys || keys.size === 0) return context;
  Object.defineProperty(context, endpointExcludedFormKeysKey, {
    value: keys,
    enumerable: false,
  });
  return context;
}

/**
 * Context copy whose `formState` omits endpoint-excluded keys.
 * Query and body keep using the original context. Headers use this copy.
 */
export function contextWithoutEndpointExcludedFormState(
  context: unknown,
): unknown {
  if (!context || typeof context !== 'object') return context;
  const record = context as Record<string | symbol, unknown>;
  const excluded = record[endpointExcludedFormKeysKey];
  if (!(excluded instanceof Set) || excluded.size === 0) return context;

  const formState = record.formState;
  if (!formState || typeof formState !== 'object') return context;

  let changed = false;
  const nextFormState: Record<string, unknown> = {
    ...(formState as Record<string, unknown>),
  };
  // `for...of` on a Set compiles to index iteration when downlevelIteration
  // is off, so the loop body never runs. forEach uses the Set method.
  excluded.forEach((key) => {
    if (typeof key !== 'string') return;
    if (!Object.prototype.hasOwnProperty.call(nextFormState, key)) return;
    delete nextFormState[key];
    changed = true;
  });
  if (!changed) return context;
  return { ...record, formState: nextFormState };
}
